import React, { useEffect, useState } from 'react';
import { Button, Box, Typography } from '@mui/material';
import StatusForm from './StatusForm.tsx';
import { Status } from './types';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ConstantActions, LeadFormActions } from '../../../Services/_actions/index.js';
import LoadingButton from '../../Shared/Loading/index.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { assignedCampaignUserAtom } from '../../../Services/_states/index.js';
import { useRecoilState, useRecoilValue } from 'recoil';
import StatusTriggersHelp from '../../Shared/HelpFIles/StatusTriggersHelp.tsx';

const validateStatusData = (status: Status): Record<string, string> => {
    const errors: Record<string, string> = {};
    const { status_name, alias_name, substatuses } = status;

    if (!status_name) {
        errors[`status_name`] = "'status_name' is required.";
    }
    if (!status?.type_of_status) {
        errors[`type_of_status`] = "'Type Of Status' is required.";
    }


    // if (!alias_name) {
    //     errors[`alias_name`] = "'alias_name' is required.";
    // }

    const substatusNamesSet = new Set();
    if (substatuses && Array.isArray(substatuses)) {
        substatuses.forEach((sub, subIndex) => {
            const subPath = `substatuses[${subIndex}].name`;
            if (!sub.name) {
                errors[subPath] = "'Substatus name' is required.";
            } else if (substatusNamesSet.has(sub.name)) {
                errors[subPath] = `'Substatus name' must be unique. Duplicate: '${sub.name}'`;
            } else {
                substatusNamesSet.add(sub.name);
            }
            if (!sub?.type_of_status) {
                errors[`substatuses[${subIndex}].type_of_status`] = "'Type Of Sub Status' is required.";
            }
        });
    }

    return errors;
};

const CreateEditStatusSubStatus: React.FC = () => {
    const { campaignId, leadFormId, statusId } = useParams();
    const constantActions = ConstantActions();
    const { showSnackBar } = useSnackBar();
    const loggedInUser = useRecoilValue(userInformationAtom);
    const organisationId = loggedInUser?.profile?.organisation?.organisation_id;
    const [userList, setUserList] = useRecoilState(assignedCampaignUserAtom);
    const navigate = useNavigate();
    const actions = LeadFormActions();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState < Status > ({
        status_name: '',
        alias_name: '',
        company_id: '',
        campaign_id: `${campaignId}`,
        leads_form_id: `${leadFormId}`,
        triggers: [],
        substatuses: [],
        active: true,
    });
    const [validationError, setValidationError] = useState({});

    const handleSubmit = async () => {
        setIsLoading(true);
        const validation = validateStatusData(status);
        setValidationError(validation);
        if (Object.keys(validation).length > 0) {
            setIsLoading(false);
            return;
        }
        const processedStatus = {
            ...status,
            substatuses: status?.substatuses?.map((sub, index) => ({
                ...sub,
                sub_status_id: sub?.sub_status_id || `${status?.status_name}_${Date.now()}_${index}`,
            })),
        };

        if (statusId) {
            try {
                const res: any = await actions.createStatusSubStatus(processedStatus);
                if (res?.status === 200) {
                    navigate(-1);
                }
            } catch (error) {
                console.error(error, "error");
            } finally {
                setIsLoading(false);
            }
        } else {
            try {
                const res: any = await actions.createStatusSubStatus(processedStatus);
                if (res?.status === 200) {
                    navigate(-1);
                }
            } catch (error) {
                console.error(error, "error");
            } finally {
                setIsLoading(false);
            }
        }
    };
    const getUserMinimalList = async () => {
        if (userList[campaignId] && userList[campaignId] <= 0) {
            return;
        }
        try {
            if (organisationId && campaignId) {
                const res: any = await constantActions.getUserMinimalList(organisationId, { campaign_id: campaignId ?? "" });
                setUserList((prev) => ({ ...prev, [campaignId]: res || [] }));
            }
        } catch (err) {
            showSnackBar(err?.toString() ?? 'Server error. Please try again.', 'error');
        }
    };

    useEffect(() => {
        if (statusId && location.state.rowData) {
            setStatus({ ...location.state.rowData })
        }
        getUserMinimalList();
    }, [statusId])

    return (
        <Box
            sx={{
                padding: "20px",
                maxWidth: "100%",
                margin: "auto",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }
            }
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "20px" }}>
                <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                        fontWeight: "bold",
                    }}
                >
                    {`${statusId ? "Update" : "Create"} Status`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <StatusTriggersHelp />
                </Box>
            </Box>

            <StatusForm
                sNo={0}
                status={status}
                onChange={(updatedStatus) => setStatus(updatedStatus)}
                onRemove={() => setStatus({
                    status_name: '',
                    alias_name: '',
                    company_id: '',
                    campaign_id: `${campaignId}`,
                    leads_form_id: `${leadFormId}`,
                    triggers: [],
                    substatuses: [],
                    active: true,
                })}
                validationError={validationError}
            />

            <Box sx={{ mt: 3 }}>
                <Button variant="outlined" size='small' onClick={() => { navigate(-1) }} sx={{ mr: 2 }}>
                    Cancel
                </Button>

                <LoadingButton
                    buttonName='Submit'
                    onClickButton={handleSubmit}
                    isLoading={isLoading}
                />
            </Box>
        </Box>
    );
};

export default CreateEditStatusSubStatus;